import { Link, NavLink } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuthStore } from '@store'
import { ROUTES } from '@constants'
import { storageURL, mediaUrl } from '@config'
import { notificationService } from '@services/api/notifications'
import { conversationService } from '@services/api/messages'

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.kudhi.app'
const APP_STORE_URL  = 'https://apps.apple.com/app/kudhi/id000000000'

function getStoreUrl() {
  const ua = navigator.userAgent
  return /iPhone|iPad|iPod|Mac/i.test(ua) ? APP_STORE_URL : PLAY_STORE_URL
}

function Header() {
  const { isAuthenticated, user, logout } = useAuthStore()
  const rawPhoto = user?.photo_profil || user?.photo || null
  const userPhoto = rawPhoto
    ? (rawPhoto.startsWith('http') ? rawPhoto
      : rawPhoto.startsWith('/storage/') ? `${storageURL.replace(/\/storage$/, '')}${rawPhoto}`
      : mediaUrl(rawPhoto))
    : null
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [notifCount, setNotifCount] = useState(0)
  const [msgCount, setMsgCount] = useState(0)

  useEffect(() => {
    if (!isAuthenticated) return

    const fetchCounts = async () => {
      try {
        const [notifRes, convRes] = await Promise.allSettled([
          notificationService.getNotifications(),
          conversationService.getConversations(),
        ])
        if (notifRes.status === 'fulfilled' && notifRes.value?.success) {
          setNotifCount(notifRes.value.data.filter(n => !n.is_read).length)
        }
        if (convRes.status === 'fulfilled' && convRes.value?.success) {
          setMsgCount(convRes.value.data.reduce((s, c) => s + (c.unread_count || 0), 0))
        }
      } catch {}
    }

    fetchCounts()
    const interval = setInterval(fetchCounts, 30000)
    return () => clearInterval(interval)
  }, [isAuthenticated])

  const handleLogout = () => {
    logout()
  }

  return (
    <header className="bg-[#FFF9E6] shadow-sm sticky top-0 z-50">
      <div className="w-[90%] md:w-[80%] mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to={ROUTES.HOME} className="flex items-center">
            <img 
              src="/Logo_LIGDI.png" 
              alt="LIGDI" 
              className="h-16 w-auto object-contain"
            />
          </Link>

          {/* Spacer pour pousser la navigation au centre-droit */}
          <div className="flex-1"></div>

          {/* Navigation desktop */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              { to: ROUTES.HOME, label: 'KUDHI', end: true },
              { to: ROUTES.PRICING, label: 'Tarification' },
            ].map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  isActive
                    ? 'text-[#D4AF37] font-bold border-b-2 border-[#D4AF37] pb-0.5 transition-colors text-lg'
                    : 'text-[#001F3F] hover:text-[#D4AF37] font-medium transition-colors text-lg'
                }
              >
                {label}
              </NavLink>
            ))}
            <NavLink
              to={ROUTES.LIGDI}
              className={({ isActive }) =>
                `flex items-center gap-1.5 font-medium transition-colors text-lg ${isActive ? 'text-[#D4AF37] font-bold border-b-2 border-[#D4AF37] pb-0.5' : 'text-[#001F3F] hover:text-[#D4AF37]'}`
              }
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              KUDHI Market
            </NavLink>
            <a href={getStoreUrl()} target="_blank" rel="noopener noreferrer" className="text-[#001F3F] hover:text-[#D4AF37] font-medium transition-colors text-lg">
              Téléchargez l'APK
            </a>
          </nav>

          {/* Actions desktop */}
          <div className="hidden md:flex items-center gap-3 ml-8">
            {isAuthenticated ? (
              <>
                {/* Mon Espace */}
                <NavLink
                  to={ROUTES.DASHBOARD}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#D4AF37] hover:bg-[#c09d2a] text-white font-semibold rounded-lg transition-colors text-base"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Mon Espace
                </NavLink>

                {/* Badge Messages */}
                <Link to={ROUTES.MESSAGES} className="relative p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <svg className="w-6 h-6 text-[#001F3F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {msgCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 leading-none">
                      {msgCount > 99 ? '99+' : msgCount}
                    </span>
                  )}
                </Link>

                {/* Badge Notifications */}
                <Link to={ROUTES.NOTIFICATIONS} className="relative p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <svg className="w-6 h-6 text-[#001F3F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {notifCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 leading-none">
                      {notifCount > 99 ? '99+' : notifCount}
                    </span>
                  )}
                </Link>

                {/* Avatar utilisateur */}
                <Link to={ROUTES.PROFILE} className="relative">
                  {userPhoto ? (
                    <img
                      src={userPhoto}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover border-2 border-[#D4AF37] hover:border-[#c09d2a] transition-colors shadow-md"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
                    />
                  ) : null}
                  <div
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] items-center justify-center text-white font-semibold text-sm border-2 border-[#D4AF37] hover:border-[#c09d2a] transition-colors shadow-md select-none"
                    style={{ display: userPhoto ? 'none' : 'flex' }}
                  >
                    {[
                      (user?.nom || user?.name || '').charAt(0),
                      (user?.prenom || '').charAt(0)
                    ].filter(Boolean).join('').toUpperCase() || 'U'}
                  </div>
                </Link>

                <button 
                  onClick={handleLogout} 
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-medium shadow-md"
                >
                  Déconnexion
                </button>
              </>
            ) : (
              <>
                <Link to={ROUTES.REGISTER} className="px-6 py-2.5 bg-white hover:bg-gray-50 text-[#001F3F] rounded-lg font-medium transition-colors border border-gray-200 text-base">
                  S'inscrire
                </Link>
                <Link
                  to={ROUTES.LOGIN}
                  className="px-6 py-2.5 rounded-lg font-bold transition-all bg-[#FFD700] text-[#001F3F] hover:bg-[#D4AF37] text-base"
                >
                  Connexion
                </Link>
              </>
            )}
          </div>

          {/* Burger menu mobile */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Menu mobile */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <nav className="flex flex-col gap-4">
              {[
                { to: ROUTES.HOME, label: 'KUDHI', end: true },
                { to: ROUTES.PRICING, label: 'Tarification' },
              ].map(({ to, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    isActive
                      ? 'text-[#D4AF37] font-bold border-l-2 border-[#D4AF37] pl-2 transition-colors'
                      : 'text-gray-700 hover:text-[#D4AF37] font-medium transition-colors'
                  }
                >
                  {label}
                </NavLink>
              ))}
              <NavLink
                to={ROUTES.LIGDI}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 font-medium transition-colors ${isActive ? 'text-[#D4AF37] font-bold border-l-2 border-[#D4AF37] pl-2' : 'text-gray-700 hover:text-[#D4AF37]'}`
                }
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                </svg>
                KUDHI Market
              </NavLink>
              <a href={getStoreUrl()} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-[#D4AF37] font-medium transition-colors">
                Téléchargez l'APK
              </a>
              {isAuthenticated && (
                <NavLink
                  to={ROUTES.DASHBOARD}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    isActive ? 'text-[#D4AF37] font-bold' : 'text-gray-700 hover:text-[#D4AF37] font-medium transition-colors'
                  }
                >
                  Mon Espace
                </NavLink>
              )}
              {isAuthenticated ? (
                <>
                  <Link to={ROUTES.MESSAGES} className="text-gray-700 hover:text-primary font-medium transition-colors flex items-center gap-2">
                    Messages
                    {msgCount > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                        {msgCount > 99 ? '99+' : msgCount}
                      </span>
                    )}
                  </Link>
                  <Link to={ROUTES.DASHBOARD} className="text-gray-700 hover:text-primary font-medium transition-colors flex items-center gap-2">
                    Tableau de bord
                  </Link>
                  <Link to={ROUTES.NOTIFICATIONS} className="text-gray-700 hover:text-primary font-medium transition-colors flex items-center gap-2">
                    Notifications
                    {notifCount > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                        {notifCount > 99 ? '99+' : notifCount}
                      </span>
                    )}
                  </Link>
                  <Link to={ROUTES.PROFILE} className="text-gray-700 hover:text-primary font-medium transition-colors">
                    Profil
                  </Link>
                  <button 
                    onClick={handleLogout} 
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-medium text-left"
                  >
                    Déconnexion
                  </button>
                </>
              ) : (
                <>
                  <Link to={ROUTES.REGISTER} className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors text-center">
                    S'inscrire
                  </Link>
                  <Link 
                    to={ROUTES.LOGIN} 
                    className="px-6 py-2 rounded-lg font-bold transition-all text-center"
                    style={{ backgroundColor: '#FFD700', color: '#001F3F' }}
                  >
                    Connexion
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
